<template>
  <div class="previewed-object" @click="onClick">
    <Suspense>
      <TaskInline
        v-if="target.kind === 'task'"
        :task-id="target.id"
        :actions="taskActions"
        compact
        preview
      />
      <ReferenceInline
        v-else-if="target.kind === 'reference'"
        :reference="target.target"
      />
      <CustomObjectInline v-else :target="target.target" />
    </Suspense>
    <ArrowUpRightIcon v-if="target.reference" class="reference-arrow" />
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";

import type { ReferenceId, TaskId } from "@ctrl/controller-rpc";

import CustomObjectInline, {
  type CustomObjectInlineTarget,
} from "@/components/graph/CustomObjectInline.vue";
import ArrowUpRightIcon from "@/components/icons/ArrowUpRightIcon.vue";
import ReferenceInline from "@/components/ReferenceInline.vue";
import type { TaskActions } from "@/components/task/types";
import TaskInline from "@/components/TaskInline.vue";

export type PreviewedObjectTarget = (
  | { kind: "object"; target: CustomObjectInlineTarget }
  | { kind: "task"; id: TaskId }
  // The generic form for reference targets that have no richer preview of
  // their own: tasks and custom objects keep the two variants above (a
  // checkbox row / the object's own inline preview) even when referenced.
  | { kind: "reference"; target: ReferenceId }
) & {
  /**
   * True when the target is only REFERENCED by the notes (an inline `#…`
   * token) rather than owned by one of their blocks: the row's icon then
   * carries the same up-right arrow badge as reference chips.
   */
  reference?: boolean;
};

const taskActions = {
  assignee: false,
  drag: false,
  hierarchy: false,
  notes: false,
  open: false,
  postpone: false,
  recurrence: false,
  schedule: { able: false },
  space: false,
  star: false,
} satisfies TaskActions;

export default defineComponent({
  name: "PreviewedObject",
  components: {
    ArrowUpRightIcon,
    CustomObjectInline,
    ReferenceInline,
    TaskInline,
  },
  props: {
    target: {
      type: Object as PropType<PreviewedObjectTarget>,
      required: true,
    },
  },
  setup() {
    const onClick = (event: MouseEvent) => {
      if (
        event.target instanceof Element &&
        event.target.closest(".context-button")
      )
        event.stopPropagation();
    };
    return { onClick, taskActions };
  },
});
</script>

<style scoped lang="scss">
.previewed-object {
  position: relative;
  width: 100%;
  min-width: 0;
  height: 17px;

  // Same badge as reference chips (see `.custom-arrow` in the editor's
  // Reference node view): the arrow sits on the icon's bottom-end corner,
  // outlined with the background color so it reads over the glyph — but
  // scaled down and toned to the row's grey icons (the chip's 9px near-black
  // arrow is proportioned for the editor's larger icons and body text).
  .reference-arrow {
    position: absolute;
    inset-inline-start: 8px;
    bottom: 1px;
    width: 7px;
    height: 7px;
    pointer-events: none;

    :deep(.fill) {
      stroke-width: 5px;
      paint-order: stroke fill;
      @include themed() {
        fill: t($grey-60);
        stroke: t($bg);
      }
    }
  }
}
</style>
